// === ENVOI FEEDBACK / SUGGESTION D'AMÉLIORATION ===
// Mirror simplifié de BugReportSender :
// - Pas de pièces jointes (screenshot/save/logs) — un retour d'amélioration n'a
//   pas besoin de l'état du jeu, juste la description et le pseudo.
// - Webhook séparé du bug report : VITE_DISCORD_FEEDBACK_WEBHOOK_URL
//   → permet de router les retours dans un canal Discord dédié (#feedback)
//   sans polluer #bugs.
// - Anti-spam local 30s comme pour les bug reports.

const FEEDBACK_KEY = 'deadtide_feedback_reports';
const MAX_FEEDBACK = 50;
const LAST_SENT_KEY = 'deadtide_feedback_last_sent';
const ANTI_SPAM_MS = 30_000;

/** Catégories pour aider le maintainer à trier les retours. */
export type FeedbackCategory = 'suggestion' | 'amelioration' | 'equilibrage' | 'autre';

export const FEEDBACK_CATEGORIES: Array<{ id: FeedbackCategory; label: string; emoji: string }> = [
  { id: 'suggestion',   label: 'Idée / suggestion', emoji: '💡' },
  { id: 'amelioration', label: 'Amélioration UI/UX', emoji: '✨' },
  { id: 'equilibrage',  label: 'Équilibrage / difficulté', emoji: '⚖️' },
  { id: 'autre',        label: 'Autre', emoji: '💬' },
];

export interface FeedbackReport {
  id: string;
  timestamp: string;
  pseudo: string;
  category: FeedbackCategory;
  message: string;
  version: string;
  scene: string;
  /** Statut d'envoi Discord pour l'historique local. */
  sent?: boolean;
  sendError?: string;
}

export interface FeedbackResult {
  ok: boolean;
  sentToDiscord: boolean;
  message: string;
}

/** Génère un ID de feedback stable. Utilisé par l'overlay pour pré-afficher l'ID. */
export function generateFeedbackId(): string {
  return `fb_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Soumet un retour : stockage local toujours, envoi Discord best-effort.
 *
 * @param presetId  Si fourni, utilisé tel quel comme `report.id`. Permet à
 *                  l'overlay d'afficher l'ID au testeur avant l'envoi.
 */
export async function submitFeedback(
  data: Omit<FeedbackReport, 'id' | 'timestamp' | 'sent' | 'sendError'>,
  presetId?: string,
): Promise<FeedbackResult> {
  // Anti-spam : 30s minimum entre 2 envois
  try {
    const last = parseInt(localStorage.getItem(LAST_SENT_KEY) ?? '0', 10);
    if (Date.now() - last < ANTI_SPAM_MS) {
      const remain = Math.ceil((ANTI_SPAM_MS - (Date.now() - last)) / 1000);
      return { ok: false, sentToDiscord: false, message: `⏳ Patiente ${remain}s avant le prochain retour.` };
    }
  } catch { /* */ }

  const report: FeedbackReport = {
    ...data,
    id: presetId ?? generateFeedbackId(),
    timestamp: new Date().toISOString(),
    sent: false,
  };

  // 1) Stockage local TOUJOURS — fallback offline + historique consultable
  try {
    const raw = localStorage.getItem(FEEDBACK_KEY);
    const all: FeedbackReport[] = raw ? JSON.parse(raw) : [];
    all.unshift(report);
    if (all.length > MAX_FEEDBACK) all.splice(MAX_FEEDBACK);
    localStorage.setItem(FEEDBACK_KEY, JSON.stringify(all));
  } catch { /* */ }

  // 2) Envoi Discord — webhook dédié, séparé du bug webhook
  const url = (import.meta.env.VITE_DISCORD_FEEDBACK_WEBHOOK_URL as string | undefined);
  if (!url) {
    return { ok: true, sentToDiscord: false, message: '✅ Retour enregistré localement (webhook non configuré).' };
  }

  try {
    const payload = buildPayload(report);
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      const errText = `Discord ${resp.status}: ${await resp.text().catch(() => '')}`.slice(0, 200);
      markStatus(report.id, false, errText);
      return { ok: true, sentToDiscord: false, message: `✅ Enregistré localement. ⚠️ ${errText}` };
    }
    markStatus(report.id, true);
    try { localStorage.setItem(LAST_SENT_KEY, String(Date.now())); } catch { /* */ }
    return { ok: true, sentToDiscord: true, message: '✅ Retour envoyé ! Merci pour le feedback 🏴‍☠️' };
  } catch (e) {
    const errText = (e instanceof Error ? e.message : String(e)).slice(0, 200);
    markStatus(report.id, false, errText);
    return { ok: true, sentToDiscord: false, message: `✅ Enregistré localement. ⚠️ Réseau : ${errText}` };
  }
}

function buildPayload(r: FeedbackReport): object {
  const cat = FEEDBACK_CATEGORIES.find(c => c.id === r.category);
  const fields = [
    { name: '📝 Message', value: r.message.slice(0, 1024), inline: false },
    { name: '🏷️ Catégorie', value: cat ? `${cat.emoji} ${cat.label}` : r.category, inline: true },
    { name: '🎮 Version', value: `v${r.version}`, inline: true },
    { name: '🎬 Scène', value: r.scene, inline: true },
  ];
  // Suffix court en titre pour repérage rapide côté Discord.
  const shortId = r.id.split('_').pop() ?? r.id;
  return {
    username: 'Deadtide Feedback',
    embeds: [{
      title: `💡 Retour #${shortId} — ${r.pseudo}`,
      color: 0xf1c40f,
      fields,
      timestamp: r.timestamp,
      footer: { text: r.id },
    }],
  };
}

function markStatus(id: string, sent: boolean, error?: string): void {
  try {
    const raw = localStorage.getItem(FEEDBACK_KEY);
    if (!raw) return;
    const all: FeedbackReport[] = JSON.parse(raw);
    const r = all.find(x => x.id === id);
    if (r) {
      r.sent = sent;
      r.sendError = error;
      localStorage.setItem(FEEDBACK_KEY, JSON.stringify(all));
    }
  } catch { /* */ }
}
